import React, { createContext, useCallback, useEffect, useRef, useState } from 'react';
import { authService } from '../services/api';
import supportService from '../services/api/supportService';
import { isSessionError } from '../services/api/sessionErrors';
import i18n from '../i18n';
import { claimOidcOwner, rotateAuthOwner } from '../pages/auth/oidcFlow';

export const AuthContext = createContext();
const IMPERSONATOR_TOKEN_KEY = 'impersonatorToken';
const normalizeUser = (user) => ({
  ...user,
  userType: user.userType || user.role || 'employee',
  // Preserve existing customer/employee sessions through the link-only rollout.
  isFirstLogin: (['admin', 'support'].includes(user.userType || user.role) || user.availableModes?.length > 0)
    && user.isFirstLogin === true,
});
const applyUserLanguage = (user) => {
  if (user?.language) Promise.resolve(i18n.changeLanguage(user.language)).catch(() => {});
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionChecking, setSessionChecking] = useState(false);
  const [sessionError, setSessionError] = useState(null);
  const [error, setError] = useState(null);
  // A response for an older identity must never overwrite a newer login/logout.
  const generation = useRef(0);

  const retrySession = useCallback(async () => {
    const requestGeneration = ++generation.current;
    const originalToken = localStorage.getItem('token');
    setSessionChecking(true);
    setSessionError(null);
    const isCurrent = () => requestGeneration === generation.current
      && localStorage.getItem('token') === originalToken;
    try {
      if (!originalToken) {
        setCurrentUser(null);
        localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
        return;
      }
      const user = await authService.getCurrentUser();
      if (!isCurrent()) return;
      if (!user) throw new Error('Missing session response');
      const normalized = normalizeUser(user);
      setCurrentUser(normalized);
      applyUserLanguage(normalized);
    } catch (err) {
      // The interceptor may already have removed an explicitly invalid token.
      if (requestGeneration !== generation.current) return;
      const savedToken = localStorage.getItem('token');
      if (savedToken && savedToken !== originalToken) return;
      setCurrentUser(null);
      if (isSessionError(err)) {
        localStorage.removeItem('token');
        localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
      } else {
        // A timeout/5xx does not establish expiry. Keep the token for Retry,
        // but expose no authenticated UI until /me verifies it.
        setSessionError(err);
      }
    } finally {
      if (requestGeneration === generation.current) setSessionChecking(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    retrySession();
    return () => { generation.current += 1; };
  }, [retrySession]);

  const authenticate = useCallback(async (operation) => {
    const requestGeneration = ++generation.current;
    const originalToken = localStorage.getItem('token'), originalOwner = localStorage.getItem('tempusAuthOwner');
    setError(null);
    try {
      const { user, token } = await operation() || {};
      if (!user || !token) throw new Error('Invalid response from server');
      if (requestGeneration !== generation.current || localStorage.getItem('token') !== originalToken
        || localStorage.getItem('tempusAuthOwner') !== originalOwner) {
        try { await authService.revokeIssuedToken(token); } catch (_) { /* revoke only the superseded issued session */ }
        throw new Error('Sign-in was superseded');
      }
      rotateAuthOwner();
      localStorage.setItem('token', token);
      const normalized = normalizeUser(user);
      setCurrentUser(normalized);
      setSessionError(null);
      setSessionChecking(false);
      applyUserLanguage(normalized);
      return normalized;
    } catch (err) {
      if (requestGeneration === generation.current) setError(err.message);
      throw err;
    } finally {
      if (requestGeneration === generation.current) setSessionChecking(false);
    }
  }, []);

  // Only bootstrap uses global loading. Forms own their pending state so a
  // request cannot unmount a form and erase its success/error feedback.
  const login = useCallback((email, password) => authenticate(() => authService.login(email, password)), [authenticate]);
  const loginWithWebauthn = useCallback((email) => authenticate(() => authService.webauthnLogin(email)), [authenticate]);
  const loginWithOidc = useCallback(() => authenticate(async () => {
    const owns = await claimOidcOwner();
    if (!owns) throw new Error('Sign-in was superseded. Start again.');
    const issued = await authService.oidcComplete();
    if (!(await owns())) {
      if (issued?.token) try { await authService.revokeIssuedToken(issued.token); } catch (_) { /* best effort */ }
      throw new Error('Sign-in was superseded. Start again.');
    }
    return issued;
  }), [authenticate]);
  const loginWithLink = useCallback((token) => authenticate(() => authService.linkLogin(token)), [authenticate]);
  const selectStaffMode = useCallback((mode) => authenticate(() => authService.selectStaffMode(mode)), [authenticate]);
  const verifyLoginLink = useCallback((token) => authenticate(() => authService.verifyLoginLink(token)), [authenticate]);
  const setFirstTimePassword = useCallback((password) => authenticate(() => authService.setFirstTimePassword(password)), [authenticate]);
  const requestLoginLink = useCallback((email) => authService.requestLoginLink(email), []);
  const requestPasswordReset = useCallback((email) => authService.requestPasswordReset(email), []);
  const resetPassword = useCallback((token, password) => authService.resetPassword(token, password), []);

  const logout = useCallback(async () => {
    const requestGeneration = ++generation.current;
    const originalToken = localStorage.getItem('token');
    rotateAuthOwner();
    setCurrentUser(null);
    setSessionError(null);
    setSessionChecking(false);
    try {
      if (originalToken) await authService.logout(originalToken);
    } catch (err) {
      if (requestGeneration === generation.current) setError(err.message);
    } finally {
      if (requestGeneration === generation.current && localStorage.getItem('token') === originalToken) {
        localStorage.removeItem('token');
      }
      if (requestGeneration === generation.current) localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
    }
  }, []);

  const startImpersonation = useCallback(async (userId) => {
    const supportToken = localStorage.getItem('token');
    const expectedGeneration = generation.current + 1;
    let childToken;
    const target = await authenticate(async () => {
      const { token, user, actingAs } = await supportService.impersonate(userId);
      childToken = token;
      return { token, user: user && { ...user, isFirstLogin: false, impersonating: true, actingAs } };
    });
    if (generation.current === expectedGeneration && localStorage.getItem('token') === childToken) {
      localStorage.setItem(IMPERSONATOR_TOKEN_KEY, supportToken);
    }
    return target;
  }, [authenticate]);

  const stopImpersonation = useCallback(async () => {
    const supportToken = localStorage.getItem(IMPERSONATOR_TOKEN_KEY);
    if (!supportToken) return logout();
    const childToken = localStorage.getItem('token');
    const requestGeneration = ++generation.current;
    rotateAuthOwner();
    localStorage.setItem('token', supportToken);
    localStorage.removeItem(IMPERSONATOR_TOKEN_KEY);
    setCurrentUser(null);
    await Promise.allSettled([
      childToken ? authService.revokeIssuedToken(childToken) : Promise.resolve(),
      supportService.stopImpersonation(currentUser?.id, supportToken),
    ]);
    if (generation.current !== requestGeneration || localStorage.getItem('token') !== supportToken) return;
    return retrySession();
  }, [currentUser, logout, retrySession]);

  return <AuthContext.Provider value={{
    currentUser, loading, error, sessionError, sessionChecking, retrySession,
    login, loginWithWebauthn, loginWithOidc, loginWithLink, selectStaffMode, verifyLoginLink, requestLoginLink,
    logout, requestPasswordReset, resetPassword, setFirstTimePassword,
    startImpersonation, stopImpersonation,
    isImpersonating: !!currentUser?.impersonating,
    actingAs: currentUser?.actingAs || null,
    clearError: () => setError(null),
    isAuthenticated: !!currentUser,
  }}>{children}</AuthContext.Provider>;
};
